import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useHistory } from 'react-router-dom';
import axios from '../../services/AxiosInstance';
import { getApiUrl } from '../../utils/ApiUtils';
import { useSelector } from 'react-redux';
import { Building2 } from 'lucide-react';
import './InvitationOnboarding.css';
import { authPageLink, forgetInvitation, invitationFromPath, rememberInvitation } from '../../utils/OnboardingRedirect';

const errorMessage = error => {
    const status = error.response?.status;
    if (error.response?.data?.code === 'RESOURCE_LIMIT_EXCEEDED') return 'Företaget har nått gränsen för antal användare. Be företagets administratör om hjälp och försök sedan igen.';
    if (status === 402) return 'Företagets abonnemang behöver uppdateras innan du kan gå med. Be företagets administratör om hjälp.';
    if (status === 403) return 'Inbjudan gäller ett annat konto eller företag. Logga in med den e-postadress som fick inbjudan.';
    if ([400, 404, 409].includes(status)) return 'Inbjudan har gått ut, återkallats eller kan inte längre användas. Be företaget om en ny inbjudan.';
    if (status === 401) return 'Logga in igen för att fortsätta med inbjudan.';
    return 'Det gick inte att behandla inbjudan. Försök igen. Om företaget redan har lagts till finns det under Mina företag.';
};

export default function AcceptInvitation() {
    const location = useLocation();
    const history = useHistory();
    const authenticated = useSelector(state => state.auth.isAuthenticated);
    const accessToken = useSelector(state => state.auth.accessToken);
    const invitation = invitationFromPath(location.pathname + location.search);
    const companyId = invitation?.companyId;
    const token = invitation?.token;
    const path = invitation?.path;
    const [details, setDetails] = useState(null);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [accepted, setAccepted] = useState(false);
    const [retry, setRetry] = useState(0);
    const submitting = useRef(false);

    useEffect(() => {
        if (path) rememberInvitation(path);
    }, [path]);

    useEffect(() => {
        let current = true;
        setDetails(null);
        setError('');
        setAccepted(false);
        if (!authenticated || !companyId || !token || !accessToken) return undefined;
        setLoading(true);
        axios.post(getApiUrl(`${companyId}/invitations/preview`), { token })
            .then(response => { if (current) setDetails(response.data); })
            .catch(failure => { if (current) setError(errorMessage(failure)); })
            .finally(() => { if (current) setLoading(false); });
        return () => { current = false; };
    }, [authenticated, accessToken, companyId, token, retry]);

    const accept = async () => {
        if (!details || submitting.current) return;
        submitting.current = true;
        setLoading(true);
        setError('');
        try {
            const response = await axios.post(getApiUrl(`${companyId}/invitations/accept`), { token });
            setDetails({ ...details, ...response.data });
            setAccepted(true);
            forgetInvitation(path);
        } catch (failure) {
            setError(errorMessage(failure));
        } finally {
            submitting.current = false;
            setLoading(false);
        }
    };

    return <main className="invitation-onboarding">
        <div className="invitation-onboarding__card">
            <div className="invitation-onboarding__brand">LedgerLoop</div>
            <div className="invitation-onboarding__icon"><Building2 size={28} aria-hidden="true" /></div>
            <h1>Välkommen till ditt företag</h1>
            {!invitation ? <p role="alert">Inbjudningslänken är ogiltig. Be företaget om en ny länk.</p>
                : !authenticated ? <>
                    <p>Logga in eller skapa ett konto med den e-postadress som fick inbjudan. Du får sedan bekräfta att du vill gå med.</p>
                    <Link className="btn btn-primary me-2" to={authPageLink('/login', path)}>Logga in</Link>
                    <Link className="btn btn-outline-primary" to={authPageLink('/page-register', path)}>Skapa konto</Link>
                </> : accepted ? <>
                    <p role="status">Du har gått med i {details.companyName}.</p>
                    {details.employeeLinkStatus === 'deferred' && <p>Företagets administratör kan hjälpa dig att koppla ditt konto till din anställning.</p>}
                    <button className="btn btn-primary" onClick={() => history.push('/select-company')}>Välj företag</button>
                </> : <>
                    {loading && <p role="status">Läser inbjudan…</p>}
                    {error && <p role="alert">{error}</p>}
                    {details && <>
                        <p>{details.alreadyAccepted ? 'Du har redan accepterat inbjudan till' : 'Du är inbjuden till'} <strong>{details.companyName}</strong>.</p>
                        {details.roles?.length > 0 && <p>Roller: {details.roles.join(', ')}</p>}
                        <button className="btn btn-primary me-2" disabled={loading} onClick={accept}>
                            {details.alreadyAccepted ? 'Fortsätt' : 'Acceptera inbjudan'}
                        </button>
                    </>}
                    {error && !details && <button className="btn btn-outline-primary me-2" onClick={() => setRetry(value => value + 1)}>Försök igen</button>}
                    <Link to="/select-company" onClick={() => forgetInvitation(path)}>Mina företag</Link>
                </>}
            <div className="invitation-onboarding__footer">Din inbjudan sparas medan du loggar in. Du väljer själv när du vill gå med i företaget.</div>
        </div>
    </main>;
}
